import type { AccionPermiso } from '@/modules/auth/contexto'

/**
 * El menú del sistema.
 *
 * Organizado por ÁREAS DE LA EMPRESA, como el esquema aprobado. Dentro de
 * Operación van las secciones numeradas del PDF, en su orden y con su número:
 * la numeración no es adorno, es la que se usa para hablar del alcance con el
 * cliente.
 *
 * FALTA LA 2 A PROPÓSITO. La trazabilidad por colaborador dejó de ser una
 * pantalla: MEGA pidió sus cifras en el tablero, que es donde se miran todos
 * los días. El número NO se corrió hacia arriba —la 3 sigue siendo el chatbot
 * y la 6 sigue siendo servicios finalizados— porque esos números son los del
 * PDF que se firmó, y renumerarlos rompería la conversación sobre el alcance.
 */

export type EntradaMenu = {
  ruta: string
  etiqueta: string
  modulo: string
  accion?: AccionPermiso
  /**
   * Qué icono la representa.
   *
   * Reemplaza a los números de sección del PDF. Servían para hablar del
   * alcance con MEGA, pero el menú ya no sigue esa numeración —la 2 se fue al
   * tablero, servicios y zonas se fueron a donde se usan— así que en pantalla
   * los números ya no correspondían a nada y las áreas comerciales nunca los
   * tuvieron. Un icono ubica de un vistazo; un número descolocado, no.
   */
  icono: string
  /** Construida y navegable, o marcador de lo que falta. */
  lista?: boolean
  /** Solo para quien tiene un colaborador vinculado, es decir, sale a campo. */
  soloCampo?: boolean
}

export type AreaMenu = {
  clave: string
  nombre: string
  entradas: EntradaMenu[]
}

/** General: lo que no pertenece a un área. */
export const GENERAL: EntradaMenu[] = [
  { ruta: '/', icono: 'tablero', etiqueta: 'Tablero', modulo: 'tablero' },
  // Solo la ve quien está vinculado a un colaborador: es su pantalla de campo,
  // no un informe. A quien no sale a la calle no le sirve de nada.
  { ruta: '/mi-trabajo', icono: 'miTrabajo', etiqueta: 'Mi trabajo', modulo: 'ordenes_trabajo', soloCampo: true },
  // Las otras dos pantallas del técnico. Van aquí solo para que la barra
  // superior sepa su título; el técnico las alcanza por el menú flotante de
  // abajo, no por el lateral. soloCampo las mantiene fuera del menú de oficina.
  { ruta: '/mi-programacion', icono: 'miTrabajo', etiqueta: 'Programación', modulo: 'ordenes_trabajo', soloCampo: true },
  { ruta: '/mi-historial', icono: 'archivo', etiqueta: 'Historial', modulo: 'ordenes_trabajo', soloCampo: true },
]

/**
 * El menú del técnico.
 *
 * MEGA lo dijo así: «ellos no necesitan ningún módulo». El operario no ve las
 * áreas de la empresa ni las secciones numeradas del PDF —esas son pantallas
 * de oficina—; ve tres cosas suyas y nada más.
 *
 * Antes le aparecían «Chatbot de WhatsApp» e «Inventario de herramientas»
 * porque su rol tiene permiso sobre ordenes_trabajo y herramientas, y el menú
 * se armaba solo con eso. El permiso decía la verdad —esos módulos tocan su
 * trabajo— pero la pantalla no es para él.
 */
export const CAMPO: EntradaMenu[] = [
  { ruta: '/mi-trabajo', icono: 'miTrabajo', etiqueta: 'Mi trabajo', modulo: 'ordenes_trabajo' },
  // «Mis herramientas» ya existe, pero NO como entrada de menú: es una sección
  // de «Mi trabajo». Un técnico lleva una o dos herramientas, y darle una
  // pantalla propia lo obligaría a navegar para ver dos renglones. Su teléfono
  // es una sola pantalla que se recorre hacia abajo.
]

export const AREAS: AreaMenu[] = [
  {
    clave: 'operacion',
    nombre: 'Operación',
    entradas: [
      {
        ruta: '/plan-proyectos',
        icono: 'tablero',
        etiqueta: 'Plan de Proyectos',
        modulo: 'tablero',
        lista: true,
      },
      {
        ruta: '/plan-proyectos?vista=apus',
        icono: 'herramienta',
        etiqueta: 'Banco de APUs',
        modulo: 'tablero',
        lista: true,
      },
      {
        ruta: '/ordenes', icono: 'matriz',
        etiqueta: 'Matriz de órdenes',
        modulo: 'ordenes_servicio',
        lista: true,
      },
      {
        ruta: '/chatbot', icono: 'chat',
        etiqueta: 'Chatbot de WhatsApp',
        modulo: 'ordenes_trabajo',
        lista: true,
      },
      // Las herramientas son de Operación, no de Bodega (Johan: «inv de
      // herramientas es de operación»): las prestan y las devuelven los
      // técnicos en sus obras.
      {
        ruta: '/herramientas', icono: 'herramienta',
        etiqueta: 'Inventario de herramientas',
        modulo: 'herramientas',
        lista: true,
      },
      {
        ruta: '/servicios-finalizados', icono: 'archivo',
        etiqueta: 'Servicios finalizados',
        modulo: 'servicios_finalizados',
        lista: true,
      },
    ],
  },

  {
    clave: 'comercial',
    nombre: 'Comercial y Clientes',
    entradas: [
      { ruta: '/clientes', icono: 'clientes', etiqueta: 'Clientes', modulo: 'maestros', lista: true },
      { ruta: '/cotizaciones', icono: 'cotizacion', etiqueta: 'Cotizaciones', modulo: 'cotizaciones', lista: true },
      { ruta: '/plan-proyectos?vista=presupuestos', icono: 'archivo', etiqueta: 'Presupuestos de Obra', modulo: 'cotizaciones', lista: true },
    ],
  },

  /*
   * FINANCIERA Y BODEGA (0245, 0246). Johan: «necesitamos una sección de
   * bodega y otra de cartera… Financiera -> Cartera, Bodega -> Alistamientos».
   * Y que se reorganice lo que ya había: las facturas de venta pasan a
   * Financiera; el inventario de materiales y la ubicación de la bodega, a
   * Bodega. El de herramientas se queda en Operación.
   * Las rutas no cambian: solo dónde salen en el menú.
   */
  {
    clave: 'financiera',
    nombre: 'Financiera',
    entradas: [
      { ruta: '/financiera/cartera', icono: 'cartera', etiqueta: 'Cartera', modulo: 'cartera', lista: true },
      { ruta: '/plan-proyectos?vista=pyg', icono: 'tablero', etiqueta: 'P&G de Proyectos', modulo: 'cartera', lista: true },
      { ruta: '/facturas', icono: 'factura', etiqueta: 'Facturas de venta', modulo: 'cotizaciones', lista: true },
    ],
  },

  {
    clave: 'bodega',
    nombre: 'Bodega',
    entradas: [
      { ruta: '/bodega/alistamientos', icono: 'alistamiento', etiqueta: 'Alistamientos', modulo: 'bodega', lista: true },
      { ruta: '/inventario', icono: 'inventario', etiqueta: 'Inventario de materiales', modulo: 'maestros', lista: true },
      // 0310: lo que sobró de los trabajos, verificado.
      { ruta: '/sobrantes', icono: 'sobrantes', etiqueta: 'Inventario de sobrantes', modulo: 'sobrantes', lista: true },
      { ruta: '/maestros/bodega', icono: 'mapa', etiqueta: 'Ubicación de la bodega', modulo: 'maestros', lista: true },
    ],
  },
]

/**
 * Configuración: los maestros que sostienen todo lo demás.
 *
 * NO ESTÁ «HERRAMIENTAS»: el catálogo es la primera mitad de la Sección 4 y
 * vive allá. Eran dos entradas de menú para la misma tabla, y una de las dos
 * abría una pantalla vacía.
 *
 * NO ESTÁ «SERVICIOS»: se crean desde la orden, que es donde uno se entera de
 * que falta uno. Once filas que se tocan dos veces al año no justifican una
 * pantalla en el menú diario.
 *
 * NO ESTÁ «ZONAS»: nacen solas de las direcciones, y su radio se corrige en la
 * programación, sobre el mapa donde se ve el círculo mal puesto. La pantalla
 * sigue existiendo para consultarlas todas, pero no en el menú: nadie entra a
 * ver zonas, entra a arreglar una que salió mal.
 *
 * Queda una sola entrada, y es la que no se puede resolver en otra parte:
 * un colaborador existe antes de que haya trabajo que asignarle.
 */
export const CONFIGURACION: EntradaMenu[] = [
  { ruta: '/maestros/colaboradores', icono: 'colaborador', etiqueta: 'Colaboradores', modulo: 'maestros', lista: true },
  // Quién hizo qué, sin poder cambiarlo (0249). Solo gerencia la ve.
  { ruta: '/auditoria', icono: 'auditoria', etiqueta: 'Auditoría', modulo: 'auditoria', lista: true },
]

/** Todas las entradas juntas, para resolver el título de la barra superior. */
export const TODAS_LAS_ENTRADAS: EntradaMenu[] = [
  ...GENERAL,
  ...AREAS.flatMap((a) => a.entradas),
  ...CONFIGURACION,
]
